/**
 * Where each cockpit tile leads, as a path and the right its screen asks for —
 * the data the Connect shell needs to offer every tile in the command palette
 * („ERP: Stammdaten › Kunden“, I-2348) before ERP itself is loaded.
 *
 * The tiles name their target by route NAME, and ERP's route names only exist in
 * the shell once ERP has been opened; a path works from the first second. The
 * path and the right come from the served route table (BLP/nx#3257): the entry
 * whose list route the tile names. `tileTargets.test.ts` fails when a tile has no
 * target, or when a target no longer matches the router it stands for.
 *
 * Kept free of route tables and views on purpose: the shell imports it eagerly
 * through `@erp/connect/shell`, BEFORE the definitions are loaded — so nothing
 * here reads them at module level; everything is a function.
 */
import { erpResourceBasePath } from '@/definitions/routeTable';
import { erpDefinitionsLoaded, erpRouteTable } from '@/definitions/store';
import type { ErpRouteTable } from '@/definitions/types';

import { masterdataResourceReadPermission } from '../resources';
import { cockpitTiles, domainLabelKey, subareaLabelKey, type CockpitTile } from './tiles';

export interface CockpitTileTarget {
  /** Path inside the ERP (`/masterdata/…`); Connect mounts it below `/erp`. */
  path: string;
  /** The read right of the target route; absent for an ungated screen. */
  requiredPermissions?: string;
}

/** List route name → target, for the resources the cockpit actually shows. */
const targetsOfTable = new WeakMap<ErpRouteTable, ReadonlyMap<string, CockpitTileTarget>>();

function listTargets(): ReadonlyMap<string, CockpitTileTarget> {
  const table = erpRouteTable();
  let targets = targetsOfTable.get(table);
  if (!targets) {
    const cockpitRoutes = new Set(cockpitTiles().map((tile) => tile.routeName));
    targets = new Map(
      Object.entries(table.resources).flatMap(([key, entry]) =>
        entry.list && cockpitRoutes.has(entry.list.name)
          ? [
              [
                entry.list.name,
                {
                  path: entry.list.path,
                  requiredPermissions: masterdataResourceReadPermission({
                    basePath: erpResourceBasePath(key),
                  }),
                },
              ] as const,
            ]
          : []
      )
    );
    targetsOfTable.set(table, targets);
  }
  return targets;
}

/** The target of one tile, or undefined when nothing is known for its route. */
export function cockpitTileTarget(
  tile: Pick<CockpitTile, 'routeName'>
): CockpitTileTarget | undefined {
  return listTargets().get(tile.routeName);
}

/** One palette command of the Stammdaten cockpit, as plain data. */
export interface MasterdataCommandTarget extends CockpitTileTarget {
  /** The tile id — the command id is `erp.masterdata.<id>`. */
  id: string;
  /** i18n key of the tile caption (ERP table). */
  labelKey: string;
  /** i18n keys of the tile's domain and subarea — searchable words. */
  domainLabelKey: string;
  subareaLabelKey: string;
}

/**
 * Every cockpit tile with a known target, in the cockpit's order. Empty until
 * the definitions are loaded; it reads the reactive `erpDefinitionsLoaded`, so a
 * computed or watcher that calls it re-evaluates when they arrive.
 */
export function masterdataCommandTargets(): readonly MasterdataCommandTarget[] {
  if (!erpDefinitionsLoaded.value) return [];
  return cockpitTiles().flatMap((tile) => {
    const target = cockpitTileTarget(tile);
    if (!target) return [];
    return [
      {
        id: tile.id,
        labelKey: tile.labelKey,
        domainLabelKey: domainLabelKey(tile.domain),
        subareaLabelKey: subareaLabelKey(tile.domain, tile.subarea),
        ...target,
      },
    ];
  });
}
